/**
 * Assertion Evaluator
 *
 * Evaluates assertions against a live Playwright page. Shared between call-time
 * (`PlaywrightClient.assert()`) and cache-replay-time validation, so both paths
 * produce identical verdicts for the same page state.
 */

import type { Page, Locator } from 'playwright';
import type { AssertionEvaluationOutcome, AssertionSpec } from '@chatsdet/types';
import { consoleLogger, type BrowserLogger } from './logger.js';
import { executeRunCode } from './playwright-client/run-code-execution.js';
import { validatePlaywrightAssertionCode } from './playwright-assertion-code.js';
import { hasStructuredPageTeardown } from './assertion-evaluation-outcome.js';

type LocatorContext = Page | ReturnType<Page['frameLocator']>;

const POSITIVE_ASSERTION_POLL_INTERVAL_MS = 200;
const NEGATIVE_ASSERTION_SETTLE_TIMEOUT_MS = 2_000;
const NEGATIVE_ASSERTION_DOM_QUIET_MS = 500;

class AssertionOperationTimeoutError extends Error {
  constructor(operation: string) {
    super(`${operation} exceeded the assertion deadline`);
    this.name = 'AssertionOperationTimeoutError';
  }
}

class AssertionOperationAbortedError extends Error {
  constructor(operation: string) {
    super(`${operation} was aborted`);
    this.name = 'AssertionOperationAbortedError';
  }
}

const runBoundedAssertionOperation = async <T>(
  operation: Promise<T>,
  deadline: number,
  signal: AbortSignal | undefined,
  label: string
): Promise<T> => {
  if (signal?.aborted) throw new AssertionOperationAbortedError(label);
  const remainingMs = deadline - Date.now();
  if (remainingMs <= 0) throw new AssertionOperationTimeoutError(label);

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    };
    const resolveOnce = (value: T) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };
    const rejectOnce = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const timeout = setTimeout(
      () => rejectOnce(new AssertionOperationTimeoutError(label)),
      remainingMs
    );
    const abort = () => rejectOnce(new AssertionOperationAbortedError(label));
    signal?.addEventListener('abort', abort, { once: true });
    operation.then(resolveOnce, rejectOnce);
  });
};

const normalizeTextAssertionCondition = (condition: string): string => {
  const trimmed = condition.trim();
  if (trimmed.length < 2) return condition;

  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  if ((first !== '"' && first !== "'") || last !== first) return condition;

  return trimmed.slice(1, -1);
};

const getAssertionContexts = (
  page: Page,
  extraFrameHints: string[] = []
): Array<{ name: string; context: LocatorContext }> => {
  const contexts: Array<{ name: string; context: LocatorContext }> = [
    { name: 'page', context: page },
  ];
  for (const hint of extraFrameHints) {
    contexts.push({ name: `iframe:${hint}`, context: page.frameLocator(hint) });
  }
  contexts.push(
    { name: 'iframe:first', context: page.frameLocator('iframe').first() },
    { name: 'iframe:nth(1)', context: page.frameLocator('iframe').nth(1) }
  );
  return contexts;
};

const pickFirstVisible = async (
  locator: Locator,
  deadline: number,
  signal?: AbortSignal,
  maxCandidates = 10
): Promise<Locator | null> => {
  const count = await runBoundedAssertionOperation(
    locator.count(),
    deadline,
    signal,
    'Locator count'
  );
  const limit = Math.min(count, maxCandidates);
  for (let i = 0; i < limit; i++) {
    const candidate = locator.nth(i);
    if (
      await runBoundedAssertionOperation(
        candidate.isVisible(),
        deadline,
        signal,
        'Locator visibility check'
      )
    ) {
      return candidate;
    }
  }
  return null;
};

const findVisibleOnceInContexts = async (
  contexts: Array<{ name: string; context: LocatorContext }>,
  makeLocator: (context: LocatorContext) => Locator,
  deadline: number,
  signal?: AbortSignal
): Promise<{ locator: Locator; contextName: string } | null> => {
  for (const { name, context } of contexts) {
    const locator = makeLocator(context);
    const visibleCandidate = await pickFirstVisible(locator, deadline, signal);
    if (visibleCandidate) {
      return { locator: visibleCandidate, contextName: name };
    }
  }
  return null;
};

const waitForNextAssertionPoll = async (
  deadline: number,
  pollIntervalMs: number,
  signal?: AbortSignal
): Promise<boolean> => {
  const remainingMs = deadline - Date.now();
  if (remainingMs <= 0) return false;

  const delayMs = Math.min(pollIntervalMs, remainingMs);
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, delayMs);
    const abort = () => {
      clearTimeout(timeout);
      reject(new AssertionOperationAbortedError('Assertion poll wait'));
    };
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
  return delayMs < remainingMs;
};

const waitForDomStable = async (page: Page, quietMs: number, timeoutMs: number): Promise<void> => {
  await page.evaluate(
    ({ quietMs: quiet, timeoutMs: timeout }) =>
      new Promise<void>((resolve) => {
        let settled = false;
        let quietTimer: ReturnType<typeof setTimeout> | undefined;
        let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
        const root = document.documentElement ?? document.body;

        const finish = () => {
          if (settled) return;
          settled = true;
          if (quietTimer) clearTimeout(quietTimer);
          if (timeoutTimer) clearTimeout(timeoutTimer);
          observer?.disconnect();
          resolve();
        };

        const scheduleQuietFinish = () => {
          if (quietTimer) clearTimeout(quietTimer);
          quietTimer = setTimeout(finish, quiet);
        };

        const observer =
          root && typeof MutationObserver !== 'undefined'
            ? new MutationObserver(scheduleQuietFinish)
            : undefined;

        if (!root || !observer) {
          scheduleQuietFinish();
          timeoutTimer = setTimeout(finish, timeout);
          return;
        }

        observer.observe(root, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
        });
        scheduleQuietFinish();
        timeoutTimer = setTimeout(finish, timeout);
      }),
    { quietMs, timeoutMs }
  );
};

const waitForNegativeAssertionSettle = async (
  page: Page,
  timeoutMs: number,
  logger: BrowserLogger
): Promise<void> => {
  const settleTimeoutMs = Math.min(timeoutMs, NEGATIVE_ASSERTION_SETTLE_TIMEOUT_MS);
  if (settleTimeoutMs <= 0) return;

  const timeout = page.waitForTimeout(settleTimeoutMs);

  const settleSignal = async (label: string, signal: Promise<unknown>) => {
    try {
      await signal;
    } catch (err) {
      if (err instanceof Error && err.name === 'TimeoutError') {
        logger.debug('[AssertionEvaluator] negative assertion settle signal timed out', {
          signal: label,
        });
        await timeout;
        return;
      }
      logger.debug('[AssertionEvaluator] negative assertion settle signal failed', {
        signal: label,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };

  await Promise.race([
    timeout,
    settleSignal(
      'dom_stable',
      waitForDomStable(page, NEGATIVE_ASSERTION_DOM_QUIET_MS, settleTimeoutMs)
    ),
    settleSignal('networkidle', page.waitForLoadState('networkidle', { timeout: settleTimeoutMs })),
  ]);
};

const findVisibleInContexts = async (
  contexts: Array<{ name: string; context: LocatorContext }>,
  makeLocator: (context: LocatorContext) => Locator,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<{ locator: Locator; contextName: string } | null> => {
  const deadline = Date.now() + timeoutMs;
  while (true) {
    const visibleCandidate = await findVisibleOnceInContexts(
      contexts,
      makeLocator,
      deadline,
      signal
    );
    if (visibleCandidate) {
      return visibleCandidate;
    }
    if (!(await waitForNextAssertionPoll(deadline, POSITIVE_ASSERTION_POLL_INTERVAL_MS, signal))) {
      return null;
    }
  }
};

const findVisibleAfterSettleInContexts = async (
  page: Page,
  contexts: Array<{ name: string; context: LocatorContext }>,
  makeLocator: (context: LocatorContext) => Locator,
  timeoutMs: number,
  logger: BrowserLogger,
  signal?: AbortSignal
): Promise<{ locator: Locator; contextName: string } | null> => {
  const deadline = Date.now() + timeoutMs;
  await runBoundedAssertionOperation(
    waitForNegativeAssertionSettle(page, timeoutMs, logger),
    deadline,
    signal,
    'Negative assertion settle'
  );
  return findVisibleOnceInContexts(contexts, makeLocator, deadline, signal);
};

export interface AssertionResult {
  passed: boolean;
  evaluationOutcome: AssertionEvaluationOutcome;
  reason?: string;
}

type UnclassifiedAssertionResult = Omit<AssertionResult, 'evaluationOutcome'> & {
  evaluationOutcome?: AssertionEvaluationOutcome;
};

export interface EvaluateAssertionOptions {
  /** Logger for diagnostic output. Defaults to consoleLogger. */
  logger?: BrowserLogger;
  /**
   * Additional iframe CSS selectors to search for text/element assertions, in
   * priority order. Callers in app contexts can pass product-specific hints
   * (e.g., embedded designer frames) without modifying this shared package.
   */
  frameHints?: string[];
  /**
   * Side-channel accessor for toasts captured by the client between snapshots.
   * `toast_present` consults this first, then falls back to the in-page
   * `window.__capturedToasts` array. The fallback is needed because each
   * snapshot capture drains the in-page array, leaving an assertion that runs
   * after a snapshot blind to toasts that fired before it.
   */
  getCapturedToasts?: () => ReadonlyArray<{ text: string }>;
  /** Abort the assertion and any in-flight Playwright predicate. */
  signal?: AbortSignal;
}

const isAssertionPrimitive = (value: unknown): value is string | number | boolean | null =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const runPlaywrightAssertionOnce = async (
  page: Page,
  code: string,
  deadline: number,
  logger: BrowserLogger,
  outerSignal?: AbortSignal
) => {
  const controller = new AbortController();
  let abortSource: 'external' | 'deadline' | null = null;
  const abort = (source: 'external' | 'deadline') => {
    if (controller.signal.aborted) return;
    abortSource = source;
    controller.abort();
  };
  const abortFromOuter = () => abort('external');
  if (outerSignal?.aborted) abortFromOuter();
  else outerSignal?.addEventListener('abort', abortFromOuter, { once: true });
  const timeout = setTimeout(() => abort('deadline'), Math.max(0, deadline - Date.now()));
  try {
    const outcome = await executeRunCode({
      page,
      code,
      signal: controller.signal,
      logger,
    });
    return { ...outcome, abortSource };
  } finally {
    clearTimeout(timeout);
    outerSignal?.removeEventListener('abort', abortFromOuter);
  }
};

/**
 * Evaluate an assertion against the current page state.
 *
 * @param page - Playwright page to evaluate against
 * @param spec - Assertion specification
 * @param timeoutMs - Timeout for waiting operations
 * @param opts - Optional logger + frameHints
 */
async function evaluateAssertionUnclassified(
  page: Page,
  spec: AssertionSpec,
  timeoutMs: number,
  opts: EvaluateAssertionOptions = {}
): Promise<UnclassifiedAssertionResult> {
  const logger = opts.logger ?? consoleLogger;
  const frameHints = opts.frameHints ?? [];

  logger.debug('[AssertionEvaluator] Evaluating assertion', {
    type: spec.type,
    condition: spec.condition,
    expected: spec.expected,
  });

  switch (spec.type) {
    case 'text_present': {
      const condition = normalizeTextAssertionCondition(spec.condition);
      const contexts = getAssertionContexts(page, frameHints);
      const result = await findVisibleInContexts(
        contexts,
        (context) => context.getByText(condition, { exact: false }),
        timeoutMs,
        opts.signal
      );
      if (result) {
        logger.debug('[AssertionEvaluator] text_present passed', {
          context: result.contextName,
        });
        return { passed: true };
      }
      logger.debug('[AssertionEvaluator] text_present failed', { condition });
      return { passed: false, reason: `Text "${condition}" not found on page` };
    }

    case 'text_absent': {
      const condition = normalizeTextAssertionCondition(spec.condition);
      const contexts = getAssertionContexts(page, frameHints);
      const result = await findVisibleAfterSettleInContexts(
        page,
        contexts,
        (context) => context.getByText(condition, { exact: false }),
        timeoutMs,
        logger,
        opts.signal
      );
      if (result) {
        logger.debug('[AssertionEvaluator] text_absent failed', {
          condition,
          context: result.contextName,
        });
        return { passed: false, reason: `Text "${condition}" is still visible on page` };
      }
      logger.debug('[AssertionEvaluator] text_absent passed');
      return { passed: true };
    }

    case 'element_visible': {
      const contexts = getAssertionContexts(page, frameHints);
      const result = await findVisibleInContexts(
        contexts,
        (context) => context.locator(spec.condition),
        timeoutMs,
        opts.signal
      );
      if (result) {
        logger.debug('[AssertionEvaluator] element_visible passed', {
          context: result.contextName,
        });
        return { passed: true };
      }
      logger.debug('[AssertionEvaluator] element_visible failed', { condition: spec.condition });
      return { passed: false, reason: `Element "${spec.condition}" not visible` };
    }

    case 'element_hidden': {
      const contexts = getAssertionContexts(page, frameHints);
      const result = await findVisibleAfterSettleInContexts(
        page,
        contexts,
        (context) => context.locator(spec.condition),
        timeoutMs,
        logger,
        opts.signal
      );
      if (result) {
        logger.debug('[AssertionEvaluator] element_hidden failed', {
          condition: spec.condition,
          context: result.contextName,
        });
        return { passed: false, reason: `Element "${spec.condition}" is still visible` };
      }
      logger.debug('[AssertionEvaluator] element_hidden passed');
      return { passed: true };
    }

    case 'toast_present': {
      const checkToasts = async (searchText: string): Promise<boolean> => {
        const needle = searchText.toLowerCase();
        // 1. Side-channel: toasts the client captured before the most recent
        //    snapshot drained the in-page array. This is the path that catches
        //    the typical click → snapshot → assert sequence.
        const sideChannel = opts.getCapturedToasts?.() ?? [];
        if (sideChannel.some((t) => t.text.toLowerCase().includes(needle))) {
          return true;
        }
        // 2. In-page array: catches toasts that fired between the last snapshot
        //    and this assertion (no snapshot in between to drain them).
        return page.evaluate((text) => {
          const toasts =
            (window as unknown as { __capturedToasts?: Array<{ text: string }> })
              .__capturedToasts || [];
          return toasts.some((t) => t.text.toLowerCase().includes(text.toLowerCase()));
        }, searchText);
      };

      if (await checkToasts(spec.condition)) {
        logger.debug('[AssertionEvaluator] toast_present passed');
        return { passed: true };
      }
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        await page.waitForTimeout(200);
        if (await checkToasts(spec.condition)) {
          logger.debug('[AssertionEvaluator] toast_present passed (after polling)');
          return { passed: true };
        }
      }
      logger.debug('[AssertionEvaluator] toast_present failed', { condition: spec.condition });
      return { passed: false, reason: `Toast "${spec.condition}" not found in captured toasts` };
    }

    case 'playwright': {
      const validation = validatePlaywrightAssertionCode(spec.condition);
      if (!validation.valid) {
        return {
          passed: false,
          evaluationOutcome: 'authored_error',
          reason: validation.reason,
        };
      }
      if (spec.expected !== undefined && !isAssertionPrimitive(spec.expected)) {
        return {
          passed: false,
          evaluationOutcome: 'authored_error',
          reason: 'Playwright assertion expected values must be string, number, boolean, or null.',
        };
      }

      // Unlike an in-page evaluate expression, a Playwright predicate can
      // semantically scope to the active dialog/listbox/row. Settle once before
      // the first observation so an absence check cannot pass in the small gap
      const deadline = Date.now() + timeoutMs;
      // Absence-like results need one bounded settle before observation so a
      // late React render cannot create a false pass. Positive predicates poll
      // immediately. Keep settle inside the assertion's overall deadline.
      if (spec.expected === 0 || spec.expected === false || spec.expected === null) {
        await waitForNegativeAssertionSettle(page, Math.floor(timeoutMs / 2), logger);
      }
      let lastResult: string | number | boolean | null | undefined;
      for (;;) {
        const outcome = await runPlaywrightAssertionOnce(
          page,
          spec.condition,
          deadline,
          logger,
          opts.signal
        );
        if (!outcome.ok) {
          const pageTornDown = hasStructuredPageTeardown(page);
          const evaluationOutcome: AssertionEvaluationOutcome = outcome.aborted
            ? outcome.abortSource === 'external' || pageTornDown
              ? 'infrastructure_error'
              : 'refuted'
            : pageTornDown
              ? 'infrastructure_error'
              : 'authored_error';
          return {
            passed: false,
            evaluationOutcome,
            reason: outcome.aborted
              ? evaluationOutcome === 'refuted'
                ? 'Playwright assertion did not satisfy before its evaluation deadline.'
                : 'Playwright assertion was externally aborted or the page was torn down.'
              : `Playwright assertion error: ${outcome.error ?? 'unknown error'}`,
          };
        }
        if (!isAssertionPrimitive(outcome.result)) {
          return {
            passed: false,
            evaluationOutcome: 'authored_error',
            reason: 'Playwright assertion code must return a string, number, boolean, or null.',
          };
        }

        lastResult = outcome.result;
        const passed =
          spec.expected !== undefined ? outcome.result === spec.expected : !!outcome.result;
        if (passed) return { passed: true };
        if (
          !(await waitForNextAssertionPoll(
            deadline,
            POSITIVE_ASSERTION_POLL_INTERVAL_MS,
            opts.signal
          ))
        ) {
          break;
        }
      }

      return spec.expected !== undefined
        ? {
            passed: false,
            reason: `Playwright assertion returned ${JSON.stringify(lastResult)}, expected ${JSON.stringify(spec.expected)}`,
          }
        : {
            passed: false,
            reason: `Playwright assertion returned falsy value: ${JSON.stringify(lastResult)}`,
          };
    }

    case 'evaluate': {
      // Poll like the other positive assertions instead of evaluating once:
      // a single-shot evaluate raced data refetches at replay (a cacheCode
      // wait can end on a URL-param change while the table refetch lands
      // ~100ms later — ledger thread 40). Conditions are documented
      // read-only deterministic predicates, so re-execution is within
      // contract. Comparison semantics are unchanged: strict equality when
      // `expected` is present (so expected:false/0/'' keep working — a
      // value MISMATCH keeps polling until the deadline), truthiness
      // otherwise. A THROWN evaluate (syntax/reference error) is a
      // deterministic authoring failure and fails immediately rather than
      // consuming the deadline.
      const deadline = Date.now() + timeoutMs;
      let lastResult: unknown;
      for (;;) {
        const result = await page.evaluate(spec.condition);

        const passed = spec.expected !== undefined ? result === spec.expected : !!result;
        if (passed) {
          logger.debug(
            spec.expected !== undefined
              ? '[AssertionEvaluator] evaluate passed'
              : '[AssertionEvaluator] evaluate passed (truthy)'
          );
          return { passed: true };
        }

        lastResult = result;
        if (
          !(await waitForNextAssertionPoll(
            deadline,
            POSITIVE_ASSERTION_POLL_INTERVAL_MS,
            opts.signal
          ))
        ) {
          break;
        }
      }

      if (spec.expected !== undefined) {
        logger.debug('[AssertionEvaluator] evaluate failed (mismatch)', {
          result: lastResult,
          expected: spec.expected,
        });
        return {
          passed: false,
          reason: `Expression returned ${JSON.stringify(lastResult)}, expected ${JSON.stringify(spec.expected)}`,
        };
      }
      logger.debug('[AssertionEvaluator] evaluate failed (falsy)', { result: lastResult });
      return {
        passed: false,
        reason: `Expression returned falsy value: ${JSON.stringify(lastResult)}`,
      };
    }

    default:
      logger.warn('[AssertionEvaluator] Unknown assertion type - failing validation', {
        type: spec.type,
      });
      throw new Error(
        `Unknown assertion type: ${spec.type}. Cache may be outdated - falling back to agent.`
      );
  }
}

export async function evaluateAssertion(
  page: Page,
  spec: AssertionSpec,
  timeoutMs: number,
  opts: EvaluateAssertionOptions = {}
): Promise<AssertionResult> {
  try {
    const result = await evaluateAssertionUnclassified(page, spec, timeoutMs, opts);
    return {
      ...result,
      evaluationOutcome: result.evaluationOutcome ?? (result.passed ? 'passed' : 'refuted'),
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const evaluationOutcome: AssertionEvaluationOutcome =
      error instanceof AssertionOperationTimeoutError ||
      error instanceof AssertionOperationAbortedError ||
      opts.signal?.aborted ||
      hasStructuredPageTeardown(page)
        ? 'infrastructure_error'
        : 'authored_error';
    (opts.logger ?? consoleLogger).debug('[AssertionEvaluator] evaluation error', {
      evaluationOutcome,
      error: errorMessage,
    });
    return {
      passed: false,
      evaluationOutcome,
      reason: `Evaluation error: ${errorMessage}`,
    };
  }
}
